import React from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useCreateCompteComptableMutation } from 'actions/plan_comptable/mutations';
import GeneriqueModal from 'components/GeneriqueModal';
import ButtonCancel from 'components/buttons/ButtonCancel';
import ButtonGreen from 'components/buttons/ButtonGreen';

const ModalAjouterCompteComptable = ({ isOpen, onClose, plan_file_id, onSuccess }) => {
  const [createCompte, { isLoading }] = useCreateCompteComptableMutation();
  const { register, handleSubmit, formState: { errors }, watch, reset } = useForm();

  const numeroCompte = watch('numero_compte');

  // Helper pour afficher la classe du compte
  const getClasseInfo = (numero) => {
    if (!numero || numero.length === 0) return null;
    const first = numero[0];
    const classes = {
      '1': { nom: 'Comptes de ressources durables', color: '#8B5CF6', bg: '#F3E8FF' },
      '2': { nom: 'Comptes d\'actif immobilisé', color: '#3B82F6', bg: '#DBEAFE' },
      '3': { nom: 'Comptes de stocks', color: '#10B981', bg: '#D1FAE5' },
      '4': { nom: 'Comptes de tiers', color: '#F59E0B', bg: '#FEF3C7' },
      '5': { nom: 'Comptes de trésorerie', color: '#06B6D4', bg: '#CFFAFE' },
      '6': { nom: 'Comptes de charges', color: '#EF4444', bg: '#FEE2E2' },
      '7': { nom: 'Comptes de produits', color: '#10B981', bg: '#D1FAE5' },
      '8': { nom: 'Comptes de résultats', color: '#6366F1', bg: '#E0E7FF' }
    };
    return classes[first] || null;
  };

  const classeInfo = numeroCompte ? getClasseInfo(numeroCompte) : null;

  const onSubmit = (data) => {
    createCompte({
      numero_compte: data.numero_compte,
      libelle: data.libelle,
      dossier_id: plan_file_id,  // En réalité c'est le dossier_id
    })
      .unwrap()
      .then((res) => {
        if (res.success) {
          toast.success(`Compte ${data.numero_compte} créé avec succès !`);
          reset();
          onSuccess(res.compte);
          onClose();
        } else {
          toast.error(res.error || "Erreur lors de la création");
        }
      })
      .catch((error) => {
        const errorMessage = error.data?.error || error.data?.message || "Erreur lors de la création du compte";
        toast.error(errorMessage);
      });
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <GeneriqueModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Créer un compte comptable"
    >
      <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        
        {/* Numéro de compte */}
        <div className="form-group">
          <label className="form-label">
            Numéro de compte <span style={{ color: 'red' }}>*</span>
          </label>
          <input
            type="text"
            className="form-control"
            placeholder="Ex: 638, 63800, 638000"
            {...register('numero_compte', {
              required: 'Le numéro de compte est obligatoire',
              pattern: {
                value: /^[1-8]\d{0,5}$/,
                message: 'Le compte doit commencer par un chiffre de 1 à 8 (max 6 chiffres)'
              }
            })}
          />
          {errors.numero_compte && (
            <span className="error-message" style={{ color: 'red', fontSize: 12, marginTop: 4, display: 'block' }}>
              {errors.numero_compte.message}
            </span>
          )}
          
          {/* Indication de la classe */}
          {classeInfo && (
            <div style={{
              marginTop: 8,
              padding: '8px 12px',
              borderRadius: 6,
              backgroundColor: classeInfo.bg,
              border: `1px solid ${classeInfo.color}44`,
              fontSize: 12,
              color: classeInfo.color
            }}>
              <strong>Classe {numeroCompte[0]}</strong> : {classeInfo.nom}
            </div>
          )}
        </div>

        {/* Libellé */}
        <div className="form-group">
          <label className="form-label">
            Libellé <span style={{ color: 'red' }}>*</span>
          </label>
          <input
            type="text"
            className="form-control"
            placeholder="Ex: Autres charges externes"
            {...register('libelle', {
              required: 'Le libellé est obligatoire',
              minLength: {
                value: 3,
                message: 'Le libellé doit contenir au moins 3 caractères'
              }
            })}
          />
          {errors.libelle && (
            <span className="error-message" style={{ color: 'red', fontSize: 12, marginTop: 4, display: 'block' }}>
              {errors.libelle.message}
            </span>
          )}
        </div>

        {/* Info SYSCOHADA */}
        <div style={{
          padding: 12,
          borderRadius: 8,
          backgroundColor: '#FFFBEB',
          border: '1px solid #FCD34D',
          fontSize: 12,
          color: '#92400E',
          lineHeight: 1.5
        }}>
          <strong>💡 Conseil :</strong> Respectez le plan comptable SYSCOHADA.
          <br />
          <strong>Classe 6</strong> = Charges, <strong>Classe 7</strong> = Produits, <strong>Classe 4</strong> = Tiers, etc.
        </div>

        {/* Footer */}
        <div className="modal-footer" style={{ marginTop: 12, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <ButtonCancel text="Annuler" onClick={handleClose} />
          <ButtonGreen
            text={isLoading ? 'Création...' : 'Créer le compte'}
            type="submit"
            disabled={isLoading}
          />
        </div>
      </form>
    </GeneriqueModal>
  );
};

export default ModalAjouterCompteComptable;
